import { useState } from 'react'
import type { BoardMember } from '../api/client'

/*
  A seat's face.

  UI SPEC §2 specified initials and no generated faces, to stop the product reading as a
  chatbot in a stock photo. The portraits override that: they are generated from each
  seat's own attribute vector, so the seat that will tell you to swing looks like it, and
  the board reads as five people rather than five monograms.

  Initials stay as the fallback rather than being deleted. A seat an owner builds from
  scratch has no portrait, and a portrait that fails to load must not leave a hole where a
  person should be.
*/

export type AvatarSize = 'sm' | 'lg' | 'xl'

export function Avatar({ member, size = 'sm' }: { member: BoardMember; size?: AvatarSize }) {
  const [failed, setFailed] = useState(false)
  const show = member.avatarUrl && !failed

  return (
    <span className={`avatar avatar--${size}`} style={ramp(member.colorRamp)}>
      {show ? (
        <img
          className="avatar__img"
          src={member.avatarUrl!}
          // The 2x file is the same crop at double the pixels. Retina and the editor's
          // larger frame both pull it; a 40px circle never does.
          srcSet={`${member.avatarUrl} 1x, ${retina(member.avatarUrl!)} 2x`}
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => setFailed(true)}
        />
      ) : (
        initials(member.displayName)
      )}
    </span>
  )
}

export function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0]!.toUpperCase()).join('')
}

function retina(url: string): string {
  return url.replace(/\.webp$/, '@2x.webp')
}

/*
  The seat's colour ramp, assigned once at creation and kept forever (UI SPEC §2). Still
  set even when a portrait renders: it backs the frame while the image loads and colours
  the initials if it never does.
*/
export function ramp(name: string): React.CSSProperties {
  return {
    ['--seat-50' as string]: `var(--${name}-50)`,
    ['--seat-500' as string]: `var(--${name}-500)`,
    ['--seat-900' as string]: `var(--${name}-900)`,
  }
}
